import React, {createContext, useContext} from "react"

/**
 * Type definition for RecaptchaV3 context value
 * @typedef {Object} RecaptchaV3ContextValue
 * @property {(action: string) => Promise<string>} execute - Execute reCAPTCHA v3 verification and return token
 */

/**
 * @type {React.Context<RecaptchaV3ContextValue | null>}
 */
const RecaptchaV3Context = createContext(
  /** @type {RecaptchaV3ContextValue | null} */ (null),
)

/**
 * Hook to access Google reCAPTCHA v3 context
 *
 * @returns {RecaptchaV3ContextValue} reCAPTCHA v3 context containing execute function
 * @throws {Error} If used outside of RecaptchaV3ContextProvider
 *
 * @example
 * const { execute } = useGoogleRecaptchaV3()
 * const token = await execute("login")
 */
export const useGoogleRecaptchaV3 = () => {
  const context = useContext(RecaptchaV3Context)

  if (!context) {
    throw new Error(
      "useGoogleRecaptchaV3 must be used within RecaptchaV3ContextProvider",
    )
  }

  return context
}

/**
 * Provider component for Google reCAPTCHA v3
 * Wraps the application to provide reCAPTCHA functionality to all child components
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {(action: string) => Promise<string>} props.execute - reCAPTCHA execute function
 * @returns {React.ReactElement}
 */
const RecaptchaV3ContextProvider = ({children, execute}) => {
  /** @type {RecaptchaV3ContextValue} */
  const value = {
    /**
     * Execute reCAPTCHA v3 verification
     * @param {string} action - The action name for this verification (e.g., "login", "signup", "submit")
     * @returns {Promise<string>} A promise that resolves to the reCAPTCHA token
     */
    execute,
  }

  return (
    <RecaptchaV3Context.Provider value={value}>
      {children}
    </RecaptchaV3Context.Provider>
  )
}

export default RecaptchaV3ContextProvider
